import React, {useState} from "react";
import {
  Box,
  Divider,
  Link as MuiLink,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import CustomSwitch from "../../../../core/switches/CustomSwitch";
import InputField from "../../../../core/inputs/InputField";
import DeviceAccessCodeItem from "../../../../core/listItems/DeviceAccessCodeItem";
import {EmptyListPanel} from "../../../../components/Helpers/EmptyPanels";
import WarningAlert from "core/alerts/WarningAlert";

// ---- Icons for statuses (green for set, yellow for in-progress, red for failed) ----
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import {createAction} from "redux/actions/experiencesActions";
import {useDispatch} from "react-redux";
import ErrorIcon from "@material-ui/icons/Error";

const useStyles = makeStyles((theme) => ({
  heading: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(4),
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  inputField: {
    flex: 1,
  },
  expandedSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  summaryBox: {
    marginBottom: theme.spacing(2),
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  // Example styling for link buttons—tweak as desired
  viewLink: {
    cursor: "pointer",
    textDecoration: "underline",
    color: theme.palette.primary.main,
  },
  summaryLabel: {
    display: "flex",
    alignItems: "center",
    // You can tweak spacing as desired:
    "& > svg": {
      marginRight: theme.spacing(1),
    },
  },
}));

export default function UserAccessCodesComponent({userInfo, setUserInfo}) {
  const classes = useStyles();
  const {t} = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(null);
  let dispatch = useDispatch();
  console.log("GOT SELECTED CATEGORY", selectedCategory);

  // ---- 1) Basic references ----
  const codes = userInfo?.access_codes || [];
  const primaryCode = userInfo?.access_code;

  // ---- 2) Derive summary data ----

  // Number of codes "set" with the primary code
  const primarySetCount = codes.filter(
    (c) => c.status === "set" && c.access_code === primaryCode,
  ).length;

  // For each inconsistent code, how many times they occur
  // "inconsistent" here means it is set but NOT the primary code
  const inconsistentCodesMap = codes.reduce((acc, c) => {
    if (c.status === "set" && c.access_code !== primaryCode) {
      acc[c.access_code] = (acc[c.access_code] || 0) + 1;
    }
    return acc;
  }, {});

  // Number of codes in a "setting" or "pending" state
  const settingCodesCount = codes.filter(
    (c) => c.status === "setting" && !c.error_message,
  ).length;

  // Number of failed codes due to device offline
  const offlineFailedCount = codes.filter(
    (c) => c?.error_message?.startsWith("Device Offline") && c.status !== "set",
  ).length;

  // Number of failed codes due to other errors
  const errorFailedCount = codes.filter(
    (c) =>
      c.status !== "set" &&
      !!c?.error_message &&
      !c?.error_message.startsWith("Device Offline"),
  ).length;

  function handleRegenerate(code_id, setLoading) {
    console.log("GOT ENSO USER ID", userInfo);
    setLoading(true);
    dispatch(
      createAction({
        enso_user_id: userInfo.enso_user_id,
        action: {
          action_id: "regenerate_access_codes",
          role: "host",
          code_id: code_id,
        },
        onSuccess: (r) => {
          console.log("GOT RESPONSE", r.action.response);
          setLoading(false);
          setUserInfo({
            ...userInfo,
            access_codes: r.action.response,
          });
        },
      }),
    );
  }

  // ---- 3) Filter logic for the "View" category ----
  const filteredCodes = (() => {
    if (!selectedCategory) return codes;
    console.log("HITTING CASE", selectedCategory, codes);
    switch (selectedCategory) {
      case "PRIMARY_SET":
        return codes.filter(
          (c) => c.status === "set" && c.access_code === primaryCode,
        );
      case "SETTING":
        return codes.filter((c) => c.status === "setting" && !c.error_message);
      case "OFFLINE_FAILED":
        return codes.filter(
          (c) =>
            c?.error_message?.startsWith("Device Offline") &&
            c.status !== "set",
        );
      case "ERROR_FAILED":
        return codes.filter(
          (c) =>
            c.status !== "set" &&
            !!c?.error_message &&
            !c?.error_message.startsWith("Device Offline"),
        );
      default:
        // "INCONSISTENT_<codeValue>"
        if (selectedCategory.startsWith("INCONSISTENT_")) {
          const codeValue = selectedCategory.replace("INCONSISTENT_", "");
          return codes.filter(
            (c) => c.status === "set" && c.access_code === codeValue,
          );
        }
        return codes;
    }
  })();

  console.log("GOT FILTERED CODES", filteredCodes);
  return (
    <>
      <Typography className={classes.heading} variant="h1">
        {t("user-device-codes-tab-heading")}
      </Typography>

      <Box className={classes.switchContainer}>
        <Typography>{t("user-device-codes-tab-description")}</Typography>
        <CustomSwitch
          alignStart
          checked={userInfo.access_code_enabled}
          onChange={() => {
            setUserInfo({
              ...userInfo,
              access_code_enabled: !userInfo.access_code_enabled,
            });
          }}
        />
      </Box>

      {userInfo.access_code_enabled && (
        <Box>
          <Typography variant="body2" color="textSecondary">
            {t("user-device-codes-tab-input-header")}
          </Typography>
          <Box className={classes.inputRow}>
            <InputField
              id="access-code"
              editOnly
              required
              numeric
              placeholder="Specify a code"
              disableEnterSave
              value={userInfo.access_code}
              className={classes.inputField}
              onChange={(e) => {
                setUserInfo({...userInfo, access_code: e.target.value});
              }}
            />
          </Box>
          <Divider />

          {/* ---- Summary of Access Codes ---- */}
          <Box className={classes.summaryBox} mt={2}>
            <Typography className={classes.heading} variant="h1">
              {t("Summary")}
            </Typography>

            {/* 1) Primary code set */}
            {primarySetCount > 0 && (
              <Box className={classes.summaryItem}>
                <Box className={classes.summaryLabel}>
                  <CheckCircleIcon style={{color: "green"}} />
                  <Typography>
                    {userInfo.access_code} (Primary Code) set on{" "}
                    {primarySetCount} devices
                  </Typography>
                </Box>
                <MuiLink
                  className={classes.viewLink}
                  onClick={() => setSelectedCategory("PRIMARY_SET")}
                >
                  {t("View")}
                </MuiLink>
              </Box>
            )}

            {/* 2) Inconsistent codes (fallback codes) */}
            {Object.entries(inconsistentCodesMap)
              .filter(([, count]) => count > 0)
              .map(([codeValue, count]) => (
                <Box key={codeValue} className={classes.summaryItem}>
                  <Box className={classes.summaryLabel}>
                    <CheckCircleIcon style={{color: "green"}} />
                    <Typography>
                      {codeValue} (Fallback code) set on {count} devices
                    </Typography>
                  </Box>
                  <MuiLink
                    className={classes.viewLink}
                    onClick={() =>
                      setSelectedCategory(`INCONSISTENT_${codeValue}`)
                    }
                  >
                    {t("View")}
                  </MuiLink>
                </Box>
              ))}

            {/* 3) Setting (or pending) codes */}
            {settingCodesCount > 0 && (
              <Box className={classes.summaryItem}>
                <Box className={classes.summaryLabel}>
                  <HourglassEmptyIcon style={{color: "goldenrod"}} />
                  <Typography>
                    Codes are setting for {settingCodesCount} devices
                  </Typography>
                </Box>
                <MuiLink
                  className={classes.viewLink}
                  onClick={() => setSelectedCategory("SETTING")}
                >
                  {t("View")}
                </MuiLink>
              </Box>
            )}

            {/* 4) Failed codes - device offline */}
            {offlineFailedCount > 0 && (
              <Box className={classes.summaryItem}>
                <Box className={classes.summaryLabel}>
                  <ErrorIcon style={{color: "red"}} />
                  <Typography>
                    Cannot generate codes on {offlineFailedCount} offline
                    devices
                  </Typography>
                </Box>
                <MuiLink
                  className={classes.viewLink}
                  onClick={() => setSelectedCategory("OFFLINE_FAILED")}
                >
                  {t("View")}
                </MuiLink>
              </Box>
            )}

            {/* 5) Failed codes - other errors */}
            {errorFailedCount > 0 && (
              <Box className={classes.summaryItem}>
                <Box className={classes.summaryLabel}>
                  <ErrorIcon style={{color: "red"}} />
                  <Typography>
                    Code failed to generate on {errorFailedCount} devices
                  </Typography>
                </Box>
                <MuiLink
                  className={classes.viewLink}
                  onClick={() => setSelectedCategory("ERROR_FAILED")}
                >
                  {t("View")}
                </MuiLink>
              </Box>
            )}
          </Box>

          {/* ---- Render the "filtered" or "full" list of codes ---- */}
          {selectedCategory && (
            <Box className={classes.expandedSection}>
              {filteredCodes.map((rc) => (
                <DeviceAccessCodeItem
                  editable
                  handleRegenerate={handleRegenerate}
                  deviceCode={rc}
                  disabled
                  visible={true}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
